import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { API, adminApi } from "./AdminDashboard";

const empty = { title: "", tag: "Community Event", date: "", time: "", location: "", description: "", highlight: "", event_date: "", link_url: "", link_label: "" };
const inputCls = "w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-2.5 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi().get(`${API}/events`).then((r) => setEvents(r.data.events || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await adminApi().put(`${API}/admin/events/${editingId}`, form);
        toast.success("Event updated.");
      } else {
        await adminApi().post(`${API}/admin/events`, form);
        toast.success("Event added.");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save event.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await adminApi().delete(`${API}/admin/events/${id}`);
      toast.success("Event deleted.");
      load();
    } catch {
      toast.error("Could not delete event.");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 lg:gap-8" data-testid="admin-events-tab">
      <form onSubmit={save} className="col-span-12 lg:col-span-5 rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-7" data-testid="admin-event-form">
        <h3 className="font-display text-2xl text-[#1A2F24]">{editingId ? "Edit Event" : "Add New Event"}</h3>
        <div className="mt-5 space-y-3">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" className={inputCls} />
          <input placeholder="Tag (e.g. Food Pantry)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} data-testid="event-form-tag" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Date label (e.g. Saturday, November 7)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-form-date" className={inputCls} />
            <input placeholder="Time (e.g. 9 AM - 12 PM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-form-time" className={inputCls} />
          </div>
          <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-form-location" className={inputCls} />
          <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" className={inputCls} />
          <input placeholder="Highlight line (optional, shown above description)" value={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.value })} data-testid="event-form-highlight" className={inputCls} />
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-1.5 ml-1">Event date — moves to Past Events after it passes</label>
            <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-form-event-date" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Button label (optional)" value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} data-testid="event-form-link-label" className={inputCls} />
            <input placeholder="Button link URL (optional)" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} data-testid="event-form-link-url" className={inputCls} />
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button disabled={saving} data-testid="event-form-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-6 py-2.5 text-xs tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {editingId ? "Save Changes" : "Add Event"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(empty); }} data-testid="event-form-cancel"
              className="inline-flex items-center gap-2 rounded-full border border-[#1E352A]/25 px-6 py-2.5 text-xs tracking-[0.12em] uppercase text-[#5C7368]">
              <X size={13} /> Cancel
            </button>
          )}
        </div>
      </form>

      <div className="col-span-12 lg:col-span-7 space-y-4" data-testid="admin-events-list">
        {events.length === 0 && <p className="text-sm text-[#5C7368]">No events yet. Add your first one.</p>}
        {events.map((ev) => (
          <div key={ev.id} className="rounded-2xl border border-[#E2E8E4] bg-white/60 p-5 flex items-start justify-between gap-4" data-testid={`admin-event-row-${ev.id}`}>
            <div>
              <span className="inline-block rounded-full bg-[#C4D7D1] px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-[#1E352A]">{ev.tag}</span>
              <h4 className="font-display text-xl mt-2 text-[#1A2F24]">{ev.title}</h4>
              <p className="text-xs text-[#5C7368] mt-1">{ev.date} · {ev.time} · {ev.location}</p>
              <p className="text-sm text-[#5C7368] mt-2">{ev.description}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setEditingId(ev.id); setForm({ title: ev.title, tag: ev.tag, date: ev.date, time: ev.time, location: ev.location, description: ev.description, highlight: ev.highlight || "", event_date: ev.event_date || "", link_url: ev.link_url || "", link_label: ev.link_label || "" }); }}
                data-testid={`admin-event-edit-${ev.id}`} aria-label="Edit event"
                className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] transition-colors duration-300">
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(ev.id)} data-testid={`admin-event-delete-${ev.id}`} aria-label="Delete event"
                className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-red-400 hover:text-red-600 transition-colors duration-300">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventsTab;
